import { Component, h, Prop, State, Event, EventEmitter, Fragment, Element } from '@stencil/core';
import AlleanzaLogo from '../../assets/images/logo-alleanza.svg';
import RefreshIcon from '../../assets/icons/refresh.svg';
import CloseIcon from '../../assets/icons/close.svg';

export enum ModalState {
  Initial = 'Initial',
  Started = 'Started',
  Refreshing = 'Refreshing',
  Refreshed = 'Refreshed',
  SliderFinishing = 'Slider finishing',
  SliderFinished = 'Slider finished',
}

export enum Breakpoint {
  Desktop,
  Tablet,
  Mobile,
}

@Component({
  tag: 'app-launch-modal',
  styleUrl: 'modal.css',
  shadow: true,
})
export class Modal {
  @Prop() mainTitle: string;
  @Prop() subtitle: string;
  @Prop() ctaPrimary: string;
  @Prop() ctaSecondary: string;
  @Prop() ctaTertiary: string;
  @Prop() slides: { title: string; paragraph: string; imageSrc: string; imageAlt: string }[];
  @Prop() qrCodeCta: string;
  @Prop() qrCodeSrc: string;
  @Prop() playStoreImageSrc: string;
  @Prop() playStoreUrl: string;
  @Prop() appStoreImageSrc: string;
  @Prop() appStoreUrl: string;
  @Prop() ctaPrimaryTargetUrl: string;
  @Prop() ctaSecondaryTargetUrl: string;
  @Prop() ctaTertiaryTargetUrl: string;

  @State() isOpen: boolean = false;
  @State() modalState: ModalState = ModalState.Initial;
  @State() breakpoint: Breakpoint = window.innerWidth < 768 ? Breakpoint.Mobile : window.innerWidth < 990 ? Breakpoint.Tablet : Breakpoint.Desktop;

  @Event() open: EventEmitter;
  @Event() close: EventEmitter;
  @Event() slideChanged: EventEmitter<number>;
  @Event() sliderEnded: EventEmitter;
  @Event() sliderRefreshed: EventEmitter;
  @Event() ctaPrimaryClicked: EventEmitter;
  @Event() ctaSecondaryClicked: EventEmitter;
  @Event() ctaTertiaryClicked: EventEmitter;
  @Event() playStoreClicked: EventEmitter;
  @Event() appStoreClicked: EventEmitter;

  @Element() hostElement: HTMLElement;

  connectedCallback() {
    this.isOpen = true;
    this.open.emit();
    window.addEventListener('resize', this.handleWindowResize.bind(this));
  }

  private handleWindowResize() {
    if (window.innerWidth < 768) {
      if (this.modalState === ModalState.SliderFinished && this.breakpoint === Breakpoint.Desktop) {
        this.handleRefresh();
      }
      if (this.breakpoint !== Breakpoint.Mobile) {
        this.breakpoint = Breakpoint.Mobile;
      }
    } else if (window.innerWidth < 990) {
      if (this.modalState === ModalState.SliderFinished && this.breakpoint === Breakpoint.Desktop) {
        this.handleRefresh();
      }
      if (this.breakpoint !== Breakpoint.Tablet) {
        this.breakpoint = Breakpoint.Tablet;
      }
    } else {
      if (this.breakpoint !== Breakpoint.Desktop) {
        this.breakpoint = Breakpoint.Desktop;
      }
    }
  }

  closeModal() {
    this.isOpen = false;
    this.close.emit();
  }

  handleAnimationStart() {
    this.modalState = ModalState.Started;
  }

  handleSliderEndStart() {
    this.modalState = ModalState.SliderFinishing;
    this.sliderEnded.emit();
  }

  handleSliderEnd() {
    this.modalState = ModalState.SliderFinished;
  }

  handleRefreshStart() {
    this.modalState = ModalState.Refreshing;
    this.sliderRefreshed.emit();
  }

  handleRefresh() {
    this.modalState = ModalState.Refreshed;
    let slider = this.hostElement.shadowRoot.querySelector('app-launch-slider');
    slider.refreshSlider();
  }

  render() {
    return (
      <div
        id="overlay"
        class={(this.isOpen ? 'open' : 'closed') + (this.modalState !== ModalState.Initial ? ' animated' : '') + (this.modalState === ModalState.Refreshed ? ' refreshed' : '')}
      >
        <div id="modal-container">
          <div id="modal-header">
            <div id="modal-header-content">
              {this.modalState !== ModalState.SliderFinished && this.modalState !== ModalState.Refreshing ? (
                <Fragment>
                  <div id="logo" innerHTML={AlleanzaLogo} key="logo-1"></div>
                  <div id="modal-header-title-container">
                    <h2 id="title" class={this.modalState === ModalState.SliderFinishing ? 'slideout' : ''}>
                      {this.mainTitle}
                    </h2>
                    <div id="subtitle" class={this.modalState === ModalState.SliderFinishing ? 'slideout' : ''}>
                      {this.subtitle}
                    </div>
                  </div>
                </Fragment>
              ) : (
                <Fragment>
                  <div
                    id="logo-2"
                    class={this.modalState === ModalState.Refreshing ? 'slideout' : ''}
                    innerHTML={AlleanzaLogo}
                    key="logo-2"
                    onAnimationEnd={() => {
                      if (this.modalState === ModalState.Refreshing) {
                        this.handleRefresh();
                      }
                    }}
                  ></div>
                  <div id="modal-header-title-container">
                    <h3 id="title-2" class={this.modalState === ModalState.Refreshing ? 'slideout' : ''}>
                      Scopri questi e molti altri contenuti scaricando l'app Alleanza.
                    </h3>
                  </div>
                </Fragment>
              )}
            </div>
            <div id="modal-header-spacer" class={this.modalState === ModalState.SliderFinished || this.modalState === ModalState.Refreshing ? 'animated' : ''}></div>
            <div id="close-button-container">
              <app-launch-text-button label="Chiudi" iconSx={CloseIcon} onClick={this.closeModal.bind(this)}></app-launch-text-button>
            </div>
          </div>
          <div id="modal-body">
            <app-launch-slider
              slides={this.slides}
              modalState={this.modalState}
              breakpoint={this.breakpoint}
              onAnimationReady={this.handleAnimationStart.bind(this)}
              onSlideChanged={event => {
                this.slideChanged.emit(event.detail);
              }}
              onSliderEndStart={this.handleSliderEndStart.bind(this)}
              onSliderEnd={this.handleSliderEnd.bind(this)}
              ctaPrimary={this.ctaPrimary}
              ctaSecondary={this.ctaSecondary}
              ctaPrimaryTargetUrl={this.ctaPrimaryTargetUrl}
              ctaSecondaryTargetUrl={this.ctaSecondaryTargetUrl}
              ctaTertiaryTargetUrl={this.ctaTertiaryTargetUrl}
              ctaTertiary={this.ctaTertiary}
              playStoreImageSrc={this.playStoreImageSrc}
              playStoreUrl={this.playStoreUrl}
              appStoreImageSrc={this.appStoreImageSrc}
              appStoreUrl={this.appStoreUrl}
              qrCodeSrc={this.qrCodeSrc}
              qrCodeCta={this.qrCodeCta}
              onCtaPrimaryClicked={() => {
                this.ctaPrimaryClicked.emit();
              }}
              onCtaSecondaryClicked={() => {
                this.ctaSecondaryClicked.emit();
              }}
              onCtaTertiaryClicked={() => {
                this.ctaTertiaryClicked.emit();
              }}
              onPlayStoreClicked={() => {
                this.playStoreClicked.emit();
              }}
              onAppStoreClicked={() => {
                this.appStoreClicked.emit();
              }}
            ></app-launch-slider>
            {(this.modalState === ModalState.SliderFinished || this.modalState === ModalState.Refreshing) && (
              <div id="refresh-button-container" class={this.modalState === ModalState.Refreshing ? 'refreshing' : ''}>
                <app-launch-text-button label="TORNA ALL'INIZIO" id="refresh-button" iconSx={RefreshIcon} onClick={this.handleRefreshStart.bind(this)}></app-launch-text-button>
              </div>
            )}
          </div>
        </div>
        {this.modalState !== ModalState.SliderFinished && this.modalState !== ModalState.Refreshing && (
          <div id="floating-cta-container">
            <div id="floating-buttons-container">
              <app-launch-button
                label={this.ctaPrimary}
                variant="filled"
                id="button-primary"
                isLink={this.breakpoint !== Breakpoint.Desktop}
                href={this.breakpoint !== Breakpoint.Desktop && this.ctaPrimaryTargetUrl}
                targetBlank
                onClick={() => {
                  if (this.breakpoint === Breakpoint.Desktop) {
                    let slider = this.hostElement.shadowRoot.querySelector('app-launch-slider');
                    slider.jumpToLastSlide();
                  }
                  this.ctaPrimaryClicked.emit();
                }}
              ></app-launch-button>
              <app-launch-text-button
                label={this.ctaSecondary}
                id="button-secondary"
                isLink
                href={this.ctaSecondaryTargetUrl}
                targetBlank
                onClick={() => {
                  this.ctaSecondaryClicked.emit();
                }}
              ></app-launch-text-button>
            </div>
          </div>
        )}
      </div>
    );
  }
}
